import React, { Fragment, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { NextSeo } from 'next-seo';
import InfiniteScroll from 'react-infinite-scroller';

import { initializeApollo } from '../../lib/auth/apollo';
import { useRouter } from 'next/router';
import { PageSpinner } from '../../components/page-spinner';
import { getNav } from '../../lib/get-nav';
import { PageLayout } from '../../components/page-layout';
import { Box, Flex, Grid, Stack } from '@chakra-ui/layout';
import { Container } from '@outdoorly/container';
import { LIST_PRODUCT_FRAGMENT } from '../products';
import { Text } from '@outdoorly/text';
import { Button } from '@outdoorly/button';
import { useAuth } from '../../providers/auth';
import { Select } from '@outdoorly/select';
import {
  ProductCardSkeleton,
  ProductGridSkeleton,
} from '../../components/skeletons';
import { makeNetworkStatus } from '../../hooks/make-network-status';
import { Alert } from '@outdoorly/alert';
import Page404 from '../404';
import { Center, GridItem, Spinner } from '@chakra-ui/react';
import {
  buildProductFeatureGridElements,
  getCarouselsByPosition,
} from '@outdoorly/util';
import { FEATURE_FRAGMENT } from '..';
import { Carousel } from '../../components/carousel';
import { ProductCardWithPreview } from '../../components/product-card-with-preview';
import { ShadowedStickyBox } from '../../components/shadowed-sticky-box';
import { NoProducts } from '../../components/no-products';
import { BrandHero } from '../../components/brands/BrandHero';
import { CategorySelector } from '../../components/brands/CategorySelector';

const GET_BRAND = gql`
  ${FEATURE_FRAGMENT}
  query Brand($slug: String!) {
    brand(slug: $slug) {
      id
      slug
      name
      squareLogoUrl
      desktopHeroUrl
      mobileHeroUrl
      descriptionLong
      shippingDescription
      tagline
      isPublished
      categoryIdsAndNamesTree
      brandCarousels(first: 10) {
        edges {
          node {
            id
            position
            carousel {
              id
              featureCarousels(first: 10) {
                edges {
                  node {
                    id
                    feature {
                      ...featureFragment
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const GET_BRAND_PRODUCTS = gql`
  query BrandProducts(
    $first: Int!
    $after: String
    $sortBy: ProductOrder
    $visibleOnStorefront: Boolean
    $filter: ProductFilterInput
  ) {
    products(
      first: $first
      after: $after
      sortBy: $sortBy
      onlyVisibleOnStorefront: $visibleOnStorefront
      filter: $filter
    ) {
      totalCount
      pageInfo {
        hasPreviousPage
        hasNextPage
        endCursor
        startCursor
      }
      edges {
        node {
          ...productFragment
        }
      }
    }
  }
  ${LIST_PRODUCT_FRAGMENT}
`;

const sortOptions = [
  { value: { field: 'DATE', direction: 'DESC' }, label: 'Newest Arrivals' },
  {
    value: { field: 'RETAIL_PRICE', direction: 'ASC' },
    label: 'Price: Low to High',
  },
  {
    value: { field: 'RETAIL_PRICE', direction: 'DESC' },
    label: 'Price: High to Low',
  },
];
const defaultSort = sortOptions[0];

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}

export async function getStaticProps() {
  const apolloClient = initializeApollo();

  await getNav(apolloClient);

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: 100,
  };
}

// export async function getServerSideProps({ params: { slug }, req }) {
//   const apolloClient = initializeApollo(null, { req });
//   await getNav(apolloClient);
//   const { data } = await apolloClient.query({
//     query: GET_BRAND,
//     variables: { slug },
//     errorPolicy: 'all',
//   });
//   if (!data.brand) {
//     return { notFound: true };
//   }
//   return {
//     props: {
//       initialApolloState: apolloClient.cache.extract(),
//       slug,
//     },
//   };
// }

const BrandPage = () => {
  const router = useRouter();
  const { isAuthed } = useAuth();
  const [sort, setSort] = useState(defaultSort.value);

  const { data, loading: brandLoading } = useQuery(GET_BRAND, {
    variables: { slug: router.query.slug },
    skip: !router.query.slug || !isAuthed,
    errorPolicy: 'all',
  });
  // We'll only get an unpublished brand if we have permissions.
  // Hence, if the the brand is unpublished, we must be admins and previewing.
  const isPreviewing = data?.brand?.isPublished === false;

  const [selectedCategory, setSelectedCategory] = useState(null);

  const {
    data: productData,
    fetchMore,
    networkStatus,
  } = useQuery(GET_BRAND_PRODUCTS, {
    variables: {
      first: 20,
      sortBy: sort,
      visibleOnStorefront: !isPreviewing,
      filter: {
        brands: [data?.brand?.id],
        isPublished: true,
        categories: selectedCategory ? [selectedCategory] : undefined,
      },
    },
    notifyOnNetworkStatusChange: true,
    skip:
      !isAuthed ||
      !router.query.slug ||
      typeof window === 'undefined' ||
      !data?.brand?.id,
  });

  const {
    loading,
    refetching,
    fetchingMore: loadingMoreProducts,
  } = makeNetworkStatus(networkStatus);

  const categories = useMemo(
    () =>
      data?.brand ? JSON.parse(data?.brand.categoryIdsAndNamesTree) ?? [] : [],
    [data?.brand]
  );

  const featureCarouselsByPosition = useMemo(
    () =>
      getCarouselsByPosition(
        data?.brand?.brandCarousels.edges.map(({ node }) => node)
      ),
    [data]
  );

  const products = useMemo(
    () => productData?.products?.edges.map(({ node }) => node) ?? [],
    [productData?.products?.edges]
  );
  const totalProducts = productData?.products?.totalCount;
  const hasNextPage = productData?.products?.pageInfo.hasNextPage;

  const gridElements = useMemo(
    () => buildProductFeatureGridElements(products, featureCarouselsByPosition),
    [products, featureCarouselsByPosition]
  );

  const numSkeletonsToShow = useMemo(
    () =>
      isPreviewing && !hasNextPage
        ? Math.max(
            Math.max(
              ...Object.keys(featureCarouselsByPosition).map(Number),
              0
            ) - totalProducts,
            0
          )
        : 0,
    [featureCarouselsByPosition, isPreviewing, hasNextPage, totalProducts]
  );

  if (router.isFallback) {
    return <PageSpinner />;
  }

  if (brandLoading) {
    return (
      <PageLayout>
        <Center flex={1}>
          <Spinner size="xl" />
        </Center>
      </PageLayout>
    );
  }

  // If the brand comes back as null (if the user cannot see it, or if it truly does not exist.)
  if (!brandLoading && data?.brand === null) {
    return <Page404 />;
  }

  return (
    <Fragment>
      <PageLayout>
        <NextSeo
          title={`${data?.brand?.name} | Outdoorly`}
          description={data?.brand?.descriptionLong}
          openGraph={{
            url: `https://www.outdoorly.com/brand/${router?.query?.slug}`,
            title: data?.brand?.name,
            description: data?.brand?.descriptionLong,
            images: [{ url: data?.brand?.desktopHeroUrl }],
          }}
        />
        <Stack spacing={6} position="relative">
          <BrandHero
            logoUrl={data?.brand.squareLogoUrl}
            name={data?.brand.name}
            tagline={data?.brand.tagline}
            description={data?.brand.descriptionLong}
            desktopHeroUrl={data?.brand.desktopHeroUrl}
            mobileHeroUrl={data?.brand.mobileHeroUrl}
            shippingDescription={data?.brand.shippingDescription}
          />

          <ShadowedStickyBox>
            <Container>
              <Stack
                p={1}
                mx="auto"
                alignItems="center"
                justifyContent="space-between"
                direction={['column', 'column', 'row']}
                spacing={0}
              >
                <CategorySelector
                  categories={categories}
                  currentCategoryId={selectedCategory}
                  onSelect={setSelectedCategory}
                />
                <Flex align="center" shrink={0}>
                  {totalProducts != null && (
                    <Text
                      color="scheme.textSecondary"
                      display={['none', 'none', 'block']}
                    >
                      {totalProducts} Results
                    </Text>
                  )}
                  {/* <StockFilter
                    onChange={setStockAvailbility}
                    checked={stockAvailability}
                  /> */}
                  <Box width="200px" ml={3}>
                    <Select
                      options={sortOptions}
                      defaultValue={defaultSort}
                      isSearchable={false}
                      onChange={(option) => {
                        setSort(option.value);
                      }}
                      styles={{ menu: (base) => ({ ...base, width: 200 }) }}
                    />
                  </Box>
                </Flex>
              </Stack>
            </Container>
          </ShadowedStickyBox>

          <Container>
            <Stack spacing={16}>
              {totalProducts === 0 && <NoProducts type="brand" />}
              {loading || refetching ? (
                <ProductGridSkeleton />
              ) : (
                <InfiniteScroll
                  threshold={500}
                  loadMore={() =>
                    fetchMore({
                      variables: {
                        first: 20,
                        after: productData?.products.pageInfo.endCursor,
                      },
                    })
                  }
                  hasMore={hasNextPage}
                >
                  <Grid
                    templateColumns={[
                      'repeat(2, minmax(0, 1fr))',
                      'repeat(2, minmax(0, 1fr))',
                      'repeat(4, minmax(0, 1fr))',
                    ]}
                    gap={4}
                  >
                    {gridElements.map((element) => {
                      if (element.carousel)
                        return (
                          <GridItem colSpan={2} key={element.carousel.id}>
                            <Carousel
                              carousel={element.carousel}
                              aspectRatio={2.05 / 1}
                            />
                          </GridItem>
                        );
                      if (element.product)
                        return (
                          <ProductCardWithPreview
                            key={element.product.id}
                            product={element.product}
                          />
                        );
                    })}
                    {!!numSkeletonsToShow &&
                      [...Array(numSkeletonsToShow)].map((_, index) => (
                        <>
                          {featureCarouselsByPosition[
                            totalProducts + index + 1
                          ] && (
                            <GridItem colSpan={2}>
                              <Carousel
                                carousel={
                                  featureCarouselsByPosition[
                                    totalProducts + index + 1
                                  ]
                                }
                                aspectRatio={2.05 / 1}
                              />
                            </GridItem>
                          )}
                          <ProductCardSkeleton isActive={false} />
                        </>
                      ))}
                  </Grid>
                </InfiniteScroll>
              )}
              <Stack spacing={5} align="center" alignSelf="center">
                {totalProducts && (
                  <Text>{`Viewing ${products.length} of ${totalProducts} from ${data?.brand.name}`}</Text>
                )}
                {hasNextPage && (
                  <Button
                    variant="solid"
                    colorScheme="red"
                    width="xs"
                    onClick={() =>
                      fetchMore({
                        variables: {
                          first: 20,
                          after: productData?.products.pageInfo.endCursor,
                        },
                      })
                    }
                    isLoading={loadingMoreProducts}
                  >
                    Load More
                  </Button>
                )}
              </Stack>
            </Stack>
          </Container>
        </Stack>
        {isPreviewing && (
          <Box position="absolute" maxW="3xl" p={2} top={0}>
            <Alert
              title="Previewing unpublished brand"
              description="You are previewing an unpublished brand's page. Only products marked as published will be shown."
              status="warning"
            />
          </Box>
        )}
      </PageLayout>
    </Fragment>
  );
};

export default BrandPage;
